import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Box,
  IconButton,
  Checkbox,
  Tooltip,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { CalendarConnection, CalendarProvider } from "../models/user";
import {
  CalendarConnectionStatusMap,
  connectionStatusKey,
} from "../util/calendarEvent";

interface ConnectCalendarDialogProps {
  onConnect: (provider: CalendarProvider, includePrivateEvents: boolean) => Promise<boolean>;
  loading?: boolean;
  error?: string | null;
  connections?: CalendarConnection[];
  onDisconnect?: (provider: string, accountUsername: string) => Promise<void>;
  onOpenDialog?: () => void;
  /** Optional per-account connection health, keyed by
   * `${provider}:${normalizedAccount}` (use `connectionStatusKey`). */
  connectionStatuses?: CalendarConnectionStatusMap;
  googleEnabled?: boolean;
  outlookEnabled?: boolean;
}

const ConnectCalendarDialog = ({
  onConnect,
  loading = false,
  error = null,
  connections = [],
  onDisconnect,
  onOpenDialog,
  connectionStatuses,
  googleEnabled = false,
  outlookEnabled = true,
}: ConnectCalendarDialogProps) => {
  const { t } = useTranslation();
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [authAccepted, setAuthAccepted] = useState(false);
  const [includePrivateEvents, setIncludePrivateEvents] = useState(true);

  const handleConnectClick = () => {
    onOpenDialog?.();
    setSelectOpen(true);
  };

  const handleProviderConnect = async () => {
    if (
      selectedProvider === CalendarProvider.Outlook ||
      selectedProvider === CalendarProvider.Google
    ) {
      const success = await onConnect(selectedProvider as CalendarProvider, includePrivateEvents);
      if (success) {
        setSelectOpen(false);
        setSelectedProvider("");
        setAuthAccepted(false);
        setIncludePrivateEvents(false);
      }
    }
  };

  const handleSelectClose = () => {
    if (!loading) {
      setSelectOpen(false);
      setSelectedProvider("");
      setAuthAccepted(false);
      setIncludePrivateEvents(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<LinkIcon />}
        onClick={handleConnectClick}
        sx={theme => ({
          borderColor: "#242C63",
          color: "#242C63",
          fontFamily: theme.typography.fontFamily,
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "6px",
          px: 2.5,
          py: 0.875,
          fontSize: "0.8125rem",

          "&:hover": {
            borderColor: "#353D7A",
            backgroundColor: "rgba(36, 44, 99, 0.04)",
          }
        })}
      >
        {t("calendar.connectButton")}
      </Button>
      {/* Provider selection + manage connected accounts dialog */}
      <Dialog
        open={selectOpen}
        onClose={handleSelectClose} slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              minWidth: 400,
              backgroundColor: "#FFFFFF",
            },
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#242C63" }}>
          {t("calendar.selectTitle")}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Connected accounts list */}
          {connections.length > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#242C63", mb: 1 }}
              >
                {t("calendar.connectedAccounts")}
              </Typography>
              {connections.map((conn) => {
                const status =
                  connectionStatuses?.[
                  connectionStatusKey(conn.provider, conn.accountUsername)
                  ];
                const statusMeta = (() => {
                  switch (status) {
                    case "active":
                      return {
                        icon: (
                          <CheckCircleIcon
                            sx={{ fontSize: 16, color: "#2E7D32" }}
                          />
                        ),
                        label: t("calendar.status.active"),
                      };
                    case "unavailable":
                      return {
                        icon: (
                          <CancelIcon
                            sx={{ fontSize: 16, color: "#C62828" }}
                          />
                        ),
                        label: t("calendar.status.unavailable"),
                      };
                    case "revoked":
                      return {
                        icon: (
                          <CancelIcon
                            sx={{ fontSize: 16, color: "#C62828" }}
                          />
                        ),
                        label: t("calendar.status.revoked"),
                      };
                    case "failed":
                      return {
                        icon: (
                          <WarningAmberIcon
                            sx={{ fontSize: 16, color: "#ED6C02" }}
                          />
                        ),
                        label: t("calendar.status.failed"),
                      };
                    default:
                      return null;
                  }
                })();

                return (
                  <Box
                    key={`${conn.provider}:${conn.accountUsername}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {statusMeta && (
                        <Tooltip title={statusMeta.label} arrow placement="top">
                          <Box sx={{ display: "inline-flex" }}>
                            {statusMeta.icon}
                          </Box>
                        </Tooltip>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {conn.accountUsername}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() =>
                        onDisconnect?.(conn.provider, conn.accountUsername)
                      }
                      disabled={loading}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "#D32F2F",
                          backgroundColor: "rgba(211, 47, 47, 0.06)",
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                );
              })}
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#242C63", mb: 1 }}
              >
                {t("calendar.addCalendar")}
              </Typography>
            </>
          )}

          {/* Provider selection */}
          <FormControl
            component="fieldset"
            sx={[{
              width: "100%"
            }, connections.length > 0 ? {
              mt: 0
            } : {
              mt: 1
            }]}
          >
            <RadioGroup
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <FormControlLabel
                value={CalendarProvider.Outlook}
                control={
                  <Radio
                    sx={{
                      color: "#242C63",
                      "&.Mui-checked": { color: "#242C63" },
                    }}
                  />
                }
                label={
                  outlookEnabled ? (
                    t("calendar.outlookLabel")
                  ) : (
                    <Typography sx={{ color: "text.disabled" }}>
                      {t("calendar.outlookLabel")}
                    </Typography>
                  )
                }
                disabled={!outlookEnabled || loading}
              />
              <FormControlLabel
                value={CalendarProvider.Google}
                control={
                  <Radio
                    sx={{
                      color: "#242C63",
                      "&.Mui-checked": { color: "#242C63" },
                    }}
                  />
                }
                label={
                  googleEnabled ? (
                    t("calendar.googleLabel")
                  ) : (
                    <Typography sx={{ color: "text.disabled" }}>
                      {t("calendar.googleLabel")}
                    </Typography>
                  )
                }
                disabled={!googleEnabled || loading}
              />
            </RadioGroup>
          </FormControl>

          {/* Authorization checkbox */}
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid hsl(210, 11%, 90%)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={authAccepted}
                  onChange={(e) => setAuthAccepted(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: "#242C63",
                    "&.Mui-checked": { color: "#242C63" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.secondary", userSelect: "none" }}>
                  {t("calendar.authorizationLabel")}
                  <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>*</Box>
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includePrivateEvents}
                  onChange={(e) => setIncludePrivateEvents(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: "#242C63",
                    "&.Mui-checked": { color: "#242C63" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.secondary", userSelect: "none" }}>
                  {t("calendar.includePrivateEvents")}
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 1,
            pt: 0,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleSelectClose}
              disabled={loading}
              sx={{
                fontWeight: 600,
                textTransform: "none",
                color: "#242C63",
              }}
            >
              {t("calendar.cancel")}
            </Button>
            <Button
              onClick={handleProviderConnect}
              variant="contained"
              disabled={
                !selectedProvider ||
                !authAccepted ||
                (!googleEnabled &&
                  selectedProvider === CalendarProvider.Google) ||
                (!outlookEnabled &&
                  selectedProvider === CalendarProvider.Outlook) ||
                loading
              }
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: "#242C63",
                "&:hover": { backgroundColor: "#353D7A" },
                "&.Mui-disabled": { backgroundColor: "#C3C7CC" },
              }}
            >
              {loading ? t("calendar.connecting") : t("calendar.connect")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConnectCalendarDialog;
